import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Helmet from 'react-helmet';
import { useSelector } from "react-redux";
import { Box, FormControlLabel, Grid, IconButton, Stack, Switch, Tooltip, Typography } from "@mui/material";
import GroupsIcon from '@mui/icons-material/Groups';
import UploadIcon from '@mui/icons-material/Upload';
import {
    Portlet, PortletBody,
    PortletHeader, PortletHeaderToolbar
} from "../../partials/content/Portlet";


import { routes } from '../../router/Routes';

import makeStyles from '@mui/styles/makeStyles';
import { BreadCrumb } from '../../common/Breadcrumb';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { TableLevelAction, useDefaultFiltersState, useTableFilter, whatChanged } from '../../common';
import { AcknowledgeDialog } from './AcknowledgeDialog';
import { FlagDialog } from './FlagDialog';

import { UpdateOutlined } from '@mui/icons-material';
import FlagIcon from '@mui/icons-material/Flag';
import GetAppIcon from '@mui/icons-material/GetApp';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import { useData, useStatePtr } from '@pure-ptr/react';
import { useFitHeight } from '../../../_metronic/layout/Layout';
import { useGlobalStore } from '../../common';
import SelectSearchTitle from '../../common/searchTitle/LocalStateSearchTitle';
import { fetchSearchCriteriaForPage } from '../../store/api/search';
import { SEARCH_PERIOD_ID } from '../../store/constants/settings';
import { fetchClaimAnalysisGridData, fetchClaimAnalysisLanding, fetchClaimAnalysisTotals } from '../../store/sagas/pages';
import { ClaimAnalysisDataGrid } from './ClaimAnalysisGrid';
import { ClaimAnalysisTopFilters } from './ClaimAnalysisTopFilters';
import { ClaimStatistics } from './ClaimStatistics';
import { ProcessClaimDialog } from './ProcessDialog';
import { IgnoreFiltersDialog } from './ServerSideSearchAndIgnoreFiltersAcknowledgeDialog';
import UpdateStatusDialog from './UpdateStatusDialog';
import { toLocalStorage } from '../../util/storage';
import ClaimAssignmentDialog  from './ClaimAssignmentDialog/ClaimAssignmentDialog';
import { getAssignedFilter } from '../../util/pagination';
import { defaultTableFilterState } from '../../common';
import { selectHasRcmTeamAccess } from '../../store/selectors/session';
import RCMTeamAssignmentDialog from './RCMTeamAssignmentDialog/RCMTeamAssignmentDialog';
import { getAllRCMTeamsList } from '../../store/api/rcmAssignment';
import { importRcmAssignmentFile } from '../../store/api/claimAnalysis';
import { errorToastify } from '../../util/toast';
import { ToastMessage } from "../../common";
import { allClaimAnalysisGridColumns } from '../../store/constants/pages';
import { fromLocalStorage } from '../../util/storage';
import ExportPopover from './ExportPopover/ExportPopover';
import { getValidUsers } from '../../store/api/claimAssignment';
import { AIChatSection } from './AIChatSection';
import { RootState } from '../../store/reducers';
import { getEAPGCreditUsage } from '../../store/api/eapg';
import { ContractInfo } from '../../store/api/ai-api';
import { ClaimAnalysisFollowUpFilter } from './ClaimAnalysisFollowUpFilter';

const useStyles = makeStyles(theme => ({
    button: {
        marginTop: theme.spacing(3),
        marginBottom: theme.spacing(1),
        marginRight: theme.spacing(1),
        zIndex: 100,
    },
    formControl: {
        margin: theme.spacing(1),
        width: 200,
    },
    formControlClaim: {
        margin: theme.spacing(1),
        width: 71
    },
    formControlStatus: {
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(1),
        marginLeft: 4,
        marginRight: 4,
        width: 140,
        '@media (max-width: 1440px)': {
            width: 110,
        }
    },
    popover: {
        pointerEvents: 'none',
    },
    paper: {
        pointerEvents: 'auto',
        width: 300,
    },
    switch: {
        marginRight: 0,
        marginTop: 20,
        marginLeft: 0
    },
    gridMDMargin: {
        '@media (max-width: 1023px)': {
            paddingLeft: 10,
        }
    },
    IconButton: {
        padding: 6
    },
    statisticsContainer:{
        padding:'20px',
        paddingBottom:0
    }
}));

const traceChanges = whatChanged();

export default function ClaimAnalysisPage({
    intl,
    claimStatusList,
    hasClaimProcessorAccess, 
    defaultCustomFilters, 
    selectedClientName,
    history,
    actions: {
        claimAnalysisSetDrilldown,
        downloadAnalysisReport,
        flagClaims,
        getTinyContracts,
        getPayersTiny,
        getClaimStatuses,
        getNpiRoles,
        resetClaimProcessError,
        setFlagToggle,
        setBulkFlagged,
        setOpenPlayDialog,
        setBulkAcknowledge,
        getFacilities,
        getUsersList,
        getPayerContractsTiny
    }
} ) {
    useFitHeight();

    const [totals, setTotals] = useState<any>();

    // Legacy redux store selectors
    const queryParams = new URLSearchParams(window.location.search);
    const preferredAssignee=queryParams.get('assignedTo')
    const [aiModeEnabled, setAiModeEnabled] = useState(false);
    const [aiCustomFilter, setAiCustomFilter] = useState('');
    const [aiMessageToSend, setAiMessageToSend] = useState('');
    const [refetchCredits, setRefetchCredits] = useState(0);
    const [aiFiltersNL, setAiFiltersNL] = useState<string>('');
    const [isProcessingAIQuery, setIsProcessingAIQuery] = useState(false);
    const [isChatExpanded, setIsChatExpanded] = useState(false);
    
    // Store filters that were applied before AI mode was enabled
    // These will be sent with every AI query
    const [preAiModeFilters, setPreAiModeFilters] = useState<any>(null);
    
    // State to trigger filters-to-nl API call
    const [filtersToConvert, setFiltersToConvert] = useState<{ filter: any; sort: any[] } | null>(null);
    
    // Store the complete AI filter structure (bypasses getPaginationFilter)
    const [aiAppliedFilters, setAiAppliedFilters] = useState<any>(null);
    
    
    // AI Access check - fetch credits and validate
    const hasClaimSemanticSearchAccess = useSelector<RootState>((state) => state.clients.hasClaimSemanticSearchAccess);
    const clientId = useSelector<RootState>((state) => state.dashboards.selectedClient);
    const role = useSelector<RootState>((state) => state.session.user?.role || null );

    
    // Fetch credit usage
    const { data: credits, isPending: isFetchingCredits } = useData(async () => {
        if (!role) return null;
        const credits = await getEAPGCreditUsage("ai");
        return credits;
    }, [role, refetchCredits]);
    
    // Check if user can use AI based on credits
    const chatBufferValue = 5; // Buffer to prevent hitting the limit exactly
    
    const haveAIAccess = useMemo(() => {
        const aiCredits = credits || {};        
        const hasCredits = Boolean(
            aiCredits?.totalLimit &&
            aiCredits?.usedCredits + chatBufferValue < aiCredits?.totalLimit
        );
        
        return Boolean(hasCredits);
    }, [credits]);

    const {
        data:rcmAssignmentData,
    } = useData(async () => {
        const allRcmTeams = await getAllRCMTeamsList();
        return allRcmTeams;
    }, []);
        

    const {
        data: userData,
    } = useData(async () => {
        const validUsers = await getValidUsers();
        return validUsers;
    }, []);

    // Fetch natural language description of filters
    const { data: filtersNLResponse } = useData(async () => {
        if (!filtersToConvert) return null;
        
        const contractInfo = new ContractInfo();
        const response = await contractInfo.filtersToNaturalLanguage(
            filtersToConvert.filter, 
            filtersToConvert.sort
        );
        
        if (response?.filters_nl) {
            setAiFiltersNL(`Applied filters: ${response.filters_nl}`);
        } else {
            setAiFiltersNL('');
        }
        
        return response;
    }, [filtersToConvert]);


    const isSummary= window.location.href.includes("fromSummary")
    const totalParams=history.location?.search?.split("&")?.length
    const user=useSelector((state: any) => state?.session?.user

    
)
    const canAccessRCM=useSelector(selectHasRcmTeamAccess)


    const claimAnalysisDownloadReportPending = useGlobalStore( x => x.claimAnalysis.download.pending );

    const relativeFilter = useGlobalStore( x => 
        x.settings.data.find( setting => 
            setting.id === SEARCH_PERIOD_ID
        )
        ?.value ?? '' 
    );

    const classes = useStyles();

    const [importFile, setImportFile] = useState(null);
    const importInputRef = useRef(null);


    function handleImportFileInput() {
        importInputRef.current?.click();
    }

    function handleFileChange(e) {
        const file = e.target.files[0];

        if (file && file.type === "text/csv") {
            setImportFile(file);
            e.target.value = null; // reset input
        } else if (file) {
            errorToastify(
                "Invalid format, please make sure the file is in CSV format."
            );
        }
    }

    const notIsSwitchingClientAsPerUserRequest = () => {
        // - Check if URL contains 'assignedTo' and 'Preferred Client' (Email Template Call) to detect client switch.
        // - Stop operations to avoid resetting states, especially after client switch.
        // - Some pages require refresh to prevent state issues (due to cache).
        // - Client switch normally pushes router to dashboard; here, we stay in Claim Analysis to refetch states.
        // - Client Switch is detected and done via Switch  Client Component and once it is done we remove the preferredClient param from the query
        return totalParams !== 2;
    };
    useEffect(() => {
        getTinyContracts();
        getClaimStatuses();
        getPayersTiny();
        getNpiRoles();
        getFacilities();
        getUsersList()
        getPayerContractsTiny()
    }, 
    [getTinyContracts, getClaimStatuses, getPayersTiny, getNpiRoles, getFacilities,getUsersList,getPayerContractsTiny]);

    useEffect(() => () => {
        claimAnalysisSetDrilldown({
            open: false,
        });
    }, []);  

    // ==========
    // Page state
    const [ tableFilter, tableFilterPtr ] = useTableFilter( 'claimAnalysis-landing', isSummary ),
        { filters, isFirstTime } = tableFilterPtr.value,
        filtersPtr = tableFilterPtr.at('filters'),
        claimPageRecord = tableFilter.pagination;

    // Persist filters to localStorage whenever they change
    // This ensures filters are available even before the first API call
    useEffect(() => {
        if (!isFirstTime && user) {
            const currentRequest = buildClaimAnalysisRequest({
                tableFilter,
                filters,
                user
            });
            
            // Persist just the filter portion for AI mode
            toLocalStorage('claimAnalysis-ai-mode-filters', currentRequest.filter);
        }
    }, [tableFilter.filters, filters.assigned, filters.significance, filters.searchTerm, user, isFirstTime]);      
        // Track previous AI mode state
    const prevAiModeEnabled = React.useRef(aiModeEnabled);
    
    useEffect(() => {
        const wasAiModeEnabled = prevAiModeEnabled.current;
        
        // AI Mode is being ENABLED
        if(aiModeEnabled && !wasAiModeEnabled){
            document.body.classList.add('ai-mode-enabled-claim-analysis');
            
            // Try to get persisted filters from localStorage first
            // This ensures filters are available even if user hasn't made changes
            let filterToUse = fromLocalStorage('claimAnalysis-ai-mode-filters');
            
            // If no persisted filters, build from current state
            if (!filterToUse) {
                const currentRequest = buildClaimAnalysisRequest({
                    tableFilter,
                    filters,
                    user,
                    skipAiFilters: true  // Skip AI filters to get actual table filters
                });
                filterToUse = currentRequest.filter;
            }
            
            // Store the filter snapshot (frozen until reset or AI mode disabled)
            setPreAiModeFilters(filterToUse);
            
            // Trigger API call to convert filters to natural language using useData
            if (filterToUse) {
                setFiltersToConvert({ filter: filterToUse, sort: tableFilter.sort || [] });
            } else {
                setAiFiltersNL('');
            }
        }
        // AI Mode is being DISABLED
        else if(!aiModeEnabled && wasAiModeEnabled){
            document.body.classList.remove('ai-mode-enabled-claim-analysis');
            setIsChatExpanded(false);
            
            // Persist AI-applied filters to localStorage so they survive page refresh
            if (aiAppliedFilters && aiAppliedFilters.filters) {
                toLocalStorage('claimAnalysis-ai-mode-filters', aiAppliedFilters.filters);
                toLocalStorage('claimAnalysis-filter-page', {
                    pagination: tableFilter.pagination,
                    sort: aiAppliedFilters.sort || [],
                    filter: aiAppliedFilters.filters,
                    significance: filters?.significance
                });
            }
            
            // Clear AI-specific UI states but KEEP aiAppliedFilters
            // This ensures the AI-applied filters remain active after AI mode is disabled
            setPreAiModeFilters(null);
            setAiFiltersNL("");
            // Note: We do NOT clear aiAppliedFilters here - the filters stay applied
        }

        // Update previous state reference
        prevAiModeEnabled.current = aiModeEnabled;

        return () => {
            document.body.classList.remove('ai-mode-enabled-claim-analysis');
            document.body.classList.remove('ai-mode-full-screen');
        };
    }, [aiModeEnabled, filters, tableFilter, user]); 


    // Callback to toggle chat expansion
    const handleChatExpandToggle = useCallback(() => {
        setIsChatExpanded(prev => {
            if(!prev) {
                document.body.classList.add('ai-mode-full-screen')
            } else {
                document.body.classList.remove('ai-mode-full-screen')
            }
            return !prev
        });
    }, []);

    // Generate dynamic placeholder based on current filters
    // Using more specific dependencies to prevent unnecessary recalculations
    // Simplified placeholder - use AI generated filters_nl when available
    const aiFilterPlaceholder = useMemo(() => {
        if(aiFiltersNL.includes("Applied filters")){
            return aiFiltersNL;
        }
        else if (aiFiltersNL) {
            return `AI applied filters: ${aiFiltersNL}`;
        }
        return "Your AI-applied filters will appear here.";
    }, [aiFiltersNL]);

    // Simple callbacks for AIInputField component
    const handleAiInputValueChange = useCallback((newValue: string) => {
        // Field is read-only, no changes allowed
    }, []);

    // Callback for chat section to update filters after AI query
    const handleAIFiltersUpdate = useCallback((filtersNL: string, filters: any, top_k?: number, sort?: any[]) => {
        // 1. Store filters_nl for placeholder
        setAiFiltersNL(`AI Applied filters: ${filtersNL || ''}`);
        
        // 2. Store the complete AI filter structure (will be used directly in buildClaimAnalysisRequest)
        setAiAppliedFilters({
            filters: filters,  // Complete AI filter structure with operands
            sort: sort || [],
            top_k: top_k
        });
        
        // 3. Update pagination only (filters will be handled by buildClaimAnalysisRequest)
        tableFilterPtr.update(prev => ({
            ...prev,
            sort: sort || [],
            pagination: {
                ...prev.pagination,
                currentPage: 1,
                recordsPerPage: top_k ? Math.min(top_k, 100) : 100
            }
        }));
        
        // Note: We do NOT update localStorage here - that only happens when:
        // 1. User manually changes filters (outside AI mode)
        // 2. User clicks reset filter button
        // 3. User turns off AI mode
    }, [tableFilterPtr]);

    const defaultFilters = useDefaultFiltersState();
    
    // Track when reset button is clicked to update preAiModeFilters after state updates
    const resetClickedRef = React.useRef(false);

    // Callback to clear all filters and reset to default criteria
    const handleClearAllFilters = useCallback(() => {
        // Clear AI filters natural language text
        setAiFiltersNL('');
        
        // Clear AI applied filters
        setAiAppliedFilters(null);
        
        // Reset to default filters
        const defaults = defaultFilters();
        filtersPtr.set(defaults);
        
        // Mark that reset was clicked so useEffect can update preAiModeFilters
        if (aiModeEnabled) {
            resetClickedRef.current = true;
        }
    }, [filtersPtr, defaultFilters, aiModeEnabled]);
    
    // Update preAiModeFilters after reset button is clicked and state updates
    useEffect(() => {
        if (resetClickedRef.current && aiModeEnabled && user) {
            // Build the filter structure with the actual current state (after reset)
            const currentRequest = buildClaimAnalysisRequest({
                tableFilter,
                filters,
                user
            });
            
            // Update preAiModeFilters with the actual default filter state
            setPreAiModeFilters(currentRequest.filter);
            
            // Also update localStorage
            toLocalStorage('claimAnalysis-ai-mode-filters', currentRequest.filter);
            
            // Trigger API call to convert filters to natural language using useData
            if (currentRequest.filter) {
                setFiltersToConvert({ filter: currentRequest.filter, sort: tableFilter.sort || [] });
            } else {
                setAiFiltersNL('');
            }
            
            // Reset the flag
            resetClickedRef.current = false;
        }
    }, [aiModeEnabled, tableFilter.filters, filters, user]);

    const userSession = useGlobalStore( x => x.session.user.id );

     const isAssignedFilterApplied = () => {
        const appliedAssignedFilters = filtersPtr?.value?.filter?.filter(
            (item) => item?.field === "assignedTo"
        );

        return appliedAssignedFilters?.length > 0;
    };


    // Fetch saved search criteria
    const { data : searchCriteriaList, isPending : isPendingSearchCriteria, reload: reloadCriteriaList } = useData( async () => {
        if( !isSummary && notIsSwitchingClientAsPerUserRequest()) {
            const [ dataForPage, defaultCriteria ] = await fetchSearchCriteriaForPage( 'claimAnalysis-landing', userSession );
            
            // First time, initialise the filter with the default filter
            if( isFirstTime 
                // If selected filter was deleted...
                || ( filters.selectedFilterId && !dataForPage.find( x => x.id === filters.selectedFilterId ) ) 
                // If default filter is applied and default filter was changed...
                || ( filters.defaultFilterId && filters.selectedFilterId === filters.defaultFilterId && filters.defaultFilterId !== defaultCriteria?.id)
            ) {
                tableFilterPtr.update( prev => ({
                    ...prev,
                    isFirstTime: false,

                    ...( defaultCriteria ? {
                        filters : {
                            ...prev.filters,
                            ...defaultCriteria.filters,
                            selectedFilterId: defaultCriteria.id,
                            defaultFilterId: defaultCriteria.id,
                        }
                    } : {
                        filters : {
                            ...defaultFilters(),
                            selectedFilterId: null,
                            defaultFilterId: null,
                        }
                    } )
                    
                }));
            }

            return dataForPage;
        }
    }
    , [totalParams]);

    const selectedCriteria = useMemo( () =>
        searchCriteriaList?.find( x => x.id === filters.selectedFilterId )
    , [ searchCriteriaList, filters.selectedFilterId ] );
    

    const {
    data,
    isPending: isClaimsPending,
    reload: reloadClaims
} = useData(async abort => {
    if( !isPendingSearchCriteria && !isFirstTime &&  notIsSwitchingClientAsPerUserRequest()) 
    {

    const request = buildClaimAnalysisRequest({
        tableFilter,
        filters,
        user
    });

    const list = await fetchClaimAnalysisGridData(
        request,
        abort
    );

    toLocalStorage(
        'claimAnalysis-backup',
        list.results.map(item => item.id)
    );

    toLocalStorage(
        'claimAnalysis-backup-page',
        list.results.map(item => ({
            id: item.id,
            page: request.pagination.currentPage
        }))
    );

    tableFilterPtr.update(prev => ({
        ...prev,
        filters: {
            ...prev.filters,
            showIgnoredFilterDialog:
                !prev.filters.ignoreAcknowledgement &&
                list.results?.length === 0 &&
                prev.filters.searchTerm?.length > 2
        },
        pagination: list.page
    }));

    return list;
}

return [];
}, [
    isPendingSearchCriteria,
    isFirstTime,
    tableFilter.filters,
    tableFilter.sort,
    tableFilter.pagination.currentPage,
    tableFilter.pagination.recordsPerPage,
    filters?.searchTerm,
    filters?.significance,
    filters?.assigned,
    totalParams,
    aiAppliedFilters  // Trigger refetch when AI applies filters
]);

const {
    isPending: isTotalsPending,
    reload: reloadTotals
} = useData(async abort => {
if( !isPendingSearchCriteria && !isFirstTime &&  notIsSwitchingClientAsPerUserRequest()) 
{
    const request = buildClaimAnalysisRequest({
        tableFilter,
        filters,
        user
    });

    const response = await fetchClaimAnalysisTotals(
        request,
        abort
    );
    setTotals(response);

    return response;
}
return {};
}, [
    isPendingSearchCriteria,
    isFirstTime,
    tableFilter.filters,
    tableFilter.sort,
    tableFilter.pagination.currentPage,
    tableFilter.pagination.recordsPerPage,
    filters?.searchTerm,
    filters?.significance,
    filters?.assigned,
    totalParams,
    aiAppliedFilters  // Trigger refetch when AI applies filters
]);



    function buildClaimAnalysisRequest({
    tableFilter,
    filters,
    user,
    skipAiFilters = false
}) {
    // If AI has applied filters, use them directly (unless we're skipping AI filters)
    if (aiAppliedFilters && !skipAiFilters) {
        return {
            pagination: tableFilter.pagination,
            sort: aiAppliedFilters.sort,
            filter: aiAppliedFilters.filters,  // Use AI filter structure as-is
            significance: filters?.searchTerm?.length > 0 ? false : filters?.significance  // Respect user's significance setting
        };
    }
    
    // Normal filter processing (when AI is not active)
    const { sort, filters: filter, pagination } = tableFilter;

    traceChanges([ isPendingSearchCriteria, isFirstTime, tableFilter.filters, tableFilter.sort, tableFilter.pagination.currentPage, tableFilter.pagination.recordsPerPage, filters?.searchTerm, filters?.significance ]);

    const assignedFilter = isAssignedFilterApplied()
        ? null
        : getAssignedFilter(user?.username);

    const newOperands = filter?.operands?.map(operand => ({
        ...operand,
        filters: operand?.filters?.map(f =>
            f.id === 'contract.name' &&
            f.value.includes(',') &&
            f.equator.includes('equal')
                ? { ...f, value: `"${f.value}"` }
                : f
        )
    }));
    
    // @ts-ignore
    let commaEscapedFilters={ ...filter, operands : (filters?.assigned && assignedFilter)  ? [...(newOperands || []),assignedFilter]:newOperands } || null;

    const finalFilter = !filter ? filters?.assigned ? commaEscapedFilters:  null : commaEscapedFilters;

    toLocalStorage('claimAnalysis-filter-page',{
                 pagination, sort,
                 filter: finalFilter,
                 significance: filters?.searchTerm?.length > 0 ? false : filters?.significance,
             }); 

    // Also persist just the filter portion for AI mode
    // This ensures filters are available when AI mode is enabled, even without user changes
    toLocalStorage('claimAnalysis-ai-mode-filters', finalFilter);

    return {
        pagination,
        sort,
        filter: finalFilter,
        significance:
            filters?.searchTerm?.length > 0
                ? false
                : filters?.significance
    };
}

    const handleRefresh = () =>{
        reloadClaims();
        reloadTotals();
    }


     const {
            data:uploadResponse,
            isPending:isPendingUpload,
            error:importError,
        } = useData(
            async () => {
                if (!importFile) return;
             const uploadResponse =  await importRcmAssignmentFile(importFile)
             handleRefresh()
             return uploadResponse
            },
            [importFile]
        );

        useEffect(() => {
            if (!importError) return;
            errorToastify(
                <ToastMessage
                    errorMessage={importError?.payload?.error}
                    innerException={importError?.payload?.innerException}
                    errorDetails={importError?.payload?.errorDetails}
                />
            );
        }, [importError]);



    const claimsData = data?.results || [],
        pending = Boolean( isClaimsPending || isPendingSearchCriteria );

    const handleAcknowledge = ({ id }) => {
        setBulkAcknowledge(false);
        selectedClaimsIdsPtr.set([id]);
        dialogsOpenPtr.at('acknowledgeDialog').set(true);
    }

    const handleAcknowledgeClose = (shouldReloadGrid) => {
        dialogsOpenPtr.at('acknowledgeDialog').set(false);
        dialogsOpenPtr.at('flagDialog').set(false);
        setFlagToggle(false);

        if (shouldReloadGrid) {
            handleRefresh();
        }
    }

    function handleBulkAcknowledge() {
        dialogsOpenPtr.at('acknowledgeDialog').set(true);
        setBulkAcknowledge(true);
    }

    function handleDowloadTemplate(onlyVisibleColumns=false,hasEapgCodeAccess) {
        let operandsWithoutBoth = tableFilter.filters?.operands.filter(operand =>
            !operand.filters.some(filter => filter.value === 'both')
        );
        let filterWithoutBoth = {
            ...tableFilter.filters,
            operands: operandsWithoutBoth || [],
        };
        const allGridColumns=allClaimAnalysisGridColumns(hasEapgCodeAccess)
       const hiddenColumns =fromLocalStorage("claimAnalysis-landing-visibility")  || {}
        const uiVisibleColumns = allGridColumns?.filter(column => {
                const key = column.enum || column.enumInGrid;
                if (!key) return true; 

                return hiddenColumns[key] !== false && !column?.hideFromGrid;
        });

        const payload = {
            page: null,
            filter: filterWithoutBoth || null,
            sort: tableFilter.sort || [],
            columns:onlyVisibleColumns ?uiVisibleColumns: allGridColumns
        };
        downloadAnalysisReport(payload, filters.significance, downloadTemplateCallback);
    }

    function downloadTemplateCallback(blob) {
        const displayName = `${selectedClientName.replace(/ /g, "_")}_ClaimAnalysisReport`;
        let url = window.URL.createObjectURL(blob);
        let a = document.createElement('a');
        a.href = url;
        a.download = `${displayName.replace(/\s/g, '')}.csv`;
        a.click();
    }

    const handleProcessClaimCallback = () => {
        setOpenPlayDialog(false);
    }

    const handleSingleFlag = (flag, { id }) => {
        // add optional date if flag is true
        if (flag) {
            dialogsOpenPtr.at('flagDialog').set(true);
            setFlagToggle(flag); // pre set in Flag Dialog
            setBulkFlagged(false);
            selectedClaimsIdsPtr.set([id]);
        } else {
            // directly call api on flag is false
            let payload = {
                analysisIds: [id],
                flag: flag,
                followupAlert: ''
            }
            flagClaims(payload, handleAcknowledgeClose, true);
        }
    }

    const handleBulkFlag = () => {
        dialogsOpenPtr.at('flagDialog').set(true);
        setBulkFlagged(true);
    }

    function handleFilterClear() {
        filtersPtr.set(defaultFilters());
    }

  function resetAllFilterSettingsToDefault() {
        const assignedFilter = getAssignedFilter(preferredAssignee, true);

        tableFilterPtr.update((prev) => ({
            ...prev,
            isFirstTime: false,

            filters: {
                ...prev?.filters,
                filter: [assignedFilter],
                customFilter: [],
                significance:false
            },
        }));
    }

    const selectedClaimsIdsPtr = useStatePtr<number[]>([]);

    const dialogsOpenPtr = useStatePtr(()=>({
        acknowledgeDialog: false,
        flagDialog: false,
        processClaimsDialog: false,
        updateStatusDialog: false,
        assignClaimsDialog:false,
        assignRcmTeamsDialog:false
    }));

    useEffect(() => {
        if (totalParams===2 && preferredAssignee) {
            resetAllFilterSettingsToDefault()
        }
    }, [totalParams]);


    return (
        <div className={'main-content-area-claim'}>
            <Helmet title="Claim Analysis" />
            <Grid container sx={{ position: "relative" }}>
                <Grid item xs={12}>
                    <BreadCrumb routes={routes} isFromClaims={true} />
                </Grid>

                <Box
                    sx={{
                        position: "absolute",
                        right: "10px",
                        display:"flex",
                        columnGap:"30px",
                    }}
                >
                    <ClaimAnalysisFollowUpFilter
                        handleRefresh={handleRefresh}
                        filtersPtr={filtersPtr}
                        pending={pending}
                        customFilter={filters.customFilter}
                        disabled={aiModeEnabled} 
                    />
                    <SelectSearchTitle
                        pending={isPendingSearchCriteria || pending}
                        searchFilters={searchCriteriaList}
                        selectedPtr={
                            filtersPtr
                                .pipe( next => ({
                                    // Assign selected search criteria to filters when selectedFilterId changes
                                    ...next,
                                    ...searchCriteriaList.find( x => x.id === next.selectedFilterId ).filters
                                }))
                                .at('selectedFilterId')
                        }
                    disabled={aiModeEnabled} 
                    />
                    {hasClaimSemanticSearchAccess && (
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={aiModeEnabled}
                                    onChange={(e) => {
                                        const newValue = e.target.checked;
                                        setAiModeEnabled(newValue);
                                        // Note: Filter clearing is handled by useEffect
                                    }}
                                    color="primary"
                                    size="small"
                                />
                            }
                            label="AI Mode"
                            sx={{
                                minWidth: "100px",
                                flexDirection: 'column-reverse',
                                margin: 0,
                                '& .MuiFormControlLabel-label': {
                                    fontSize: '11px',
                                    color: aiModeEnabled ? '#667eea' : 'inherit'
                                }
                            }}
                        />
                    )}
                </Box>

            </Grid>


            {/* Main content wrapper with AI Chat sidebar */}
            <Box sx={{ 
                display: 'flex', 
                flexDirection: 'row', 
                width: '100%', 
                gap: 0,
            }}>
                {/* Main content area - takes remaining space */}
                <Box sx={{ 
                    flex: isChatExpanded ? 0 : 1,
                    minWidth: 0,
                    opacity: isChatExpanded ? 0 : 1,
                    width: isChatExpanded ? 0 : 'auto',
                    transition: 'all 0.3s ease-in-out',
                }}>
            <Portlet>
                <Box className={classes.statisticsContainer}>
                    <ClaimStatistics
                        data={totals}
                        pending={isTotalsPending}
                    />
                </Box>

                <Box sx={aiModeEnabled ? { 
                            '& .MuiIconButton-root[color="error"], & .filter-clear-btn, & [class*="deleteIcon"], & button:has(svg[data-testid="DeleteIcon"])': { 
                                display: 'none !important' 
                            },
                            '& .kt-portlet__head-toolbar': {
                                flex: 1,
                                width: '100%',
                            }
                        } : {}}>
                <PortletHeader
                    pending={isClaimsPending!=null}
                    title="Claim Analysis"
                    name="claimAnalysis-landing"
                    handleRefresh={handleRefresh}
                    showSearchFilters={!aiModeEnabled}
                    filter={aiModeEnabled ? [] : filters.filter}
                    handleFilterClear={aiModeEnabled ? undefined : handleFilterClear}
                    includeAdvanceFilters={!aiModeEnabled}
                    toolbar={
                         <PortletHeaderToolbar style={aiModeEnabled ? { width: '100%', flex: 1 } : {}}>
                                    {aiModeEnabled ? (
                                        <Stack direction="column" sx={{ width: '100%' }}>
                                            {/* Read-only text display for applied filters */}
                                            <Box
                                                sx={{
                                                    width: '100%',
                                                    padding: '12px 16px',
                                                    backgroundColor: '#f5f5f5',
                                                    borderRadius: '8px',
                                                    border: '1px solid #e0e0e0',
                                                    minHeight: '48px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                }}
                                            >
                                                <Typography
                                                    sx={{
                                                        fontSize: '14px',
                                                        color: aiFiltersNL ? '#333' : '#999',
                                                        fontStyle: aiFiltersNL ? 'normal' : 'italic',
                                                    }}
                                                >
                                                    {aiFilterPlaceholder}
                                                </Typography>
                                            </Box>
                                            {/* Error message when no AI access */}
                                            {!haveAIAccess && !isFetchingCredits && (
                                                <Typography sx={{ color: 'red', fontSize: '12px', mt: 0.5, ml: 1 }}>
                                                    <b>Note:</b>{" "}
                                                    {!hasClaimSemanticSearchAccess
                                                        ? "AI Features are disabled for this client. Contact admin."
                                                        : "You don't have enough credits to use this feature. Contact the admin to upgrade."}
                                                </Typography>
                                            )}
                                        </Stack>
                                    ) : (
                                        <ClaimAnalysisTopFilters pendingState={ pending } handleRefresh={handleRefresh} relativeFilter={relativeFilter} tableFiltersPtr={ filtersPtr }/>
                                    )}
                        </PortletHeaderToolbar>
                    }
                />
                </Box>


                <PortletBody>
                    <Stack direction="column" spacing={1} sx={{ height: 'calc(100vh - 420px)', '@media (max-width: 1550px)': { height: 'calc(100vh - 493px)' } }}>
                        <TableLevelAction
                            handleRefresh={handleRefresh}
                            actions={ () =>
                                <>
                                    <Grid item>
                                        <Tooltip title="Update Status">
                                            <IconButton aria-label="process" size='small' onClick={() => dialogsOpenPtr.at('updateStatusDialog').set(true) } disabled={claimAnalysisDownloadReportPending}>
                                                <UpdateOutlined />
                                            </IconButton>
                                        </Tooltip>
                                    </Grid>

                                    {(canAccessRCM) && 
                                    <Grid item>
                                        <Tooltip title="Assign RCM Team">
                                            <IconButton aria-label="process" size='small' onClick={() => dialogsOpenPtr.at('assignRcmTeamsDialog').set(true) }>
                                                <GroupsIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </Grid>}

                                    {hasClaimProcessorAccess && (
                                        <Grid item>
                                            <Tooltip title="Process Claims">
                                                <IconButton aria-label="process" size='small' 
                                                    onClick={ () => dialogsOpenPtr.at('processClaimsDialog').set(true) } 
                                                    disabled={claimAnalysisDownloadReportPending}
                                                >
                                                    <PlayCircleOutlineIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </Grid>
                                    )}


                                        <Grid item>
                                            <Tooltip title="Assign Claims">
                                                <IconButton aria-label="process" size='small' 
                                                    onClick={() => dialogsOpenPtr.at('assignClaimsDialog').set(true) } 
                                                    disabled={claimAnalysisDownloadReportPending}
                                                >
                                                    <PersonAddAltIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </Grid>

                                        {/* Import Button */}
                                       {canAccessRCM && <Grid item>
                                            <Tooltip title="Import RCM Teams">
                                                <IconButton
                                                    aria-label="import"
                                                    size="small"
                                                    onClick={handleImportFileInput}
                                                    disabled={claimAnalysisDownloadReportPending}
                                                >
                                                    <UploadIcon />
                                                    <input
                                                        type="file"
                                                        hidden
                                                        ref={importInputRef}
                                                        onChange={handleFileChange}
                                                        accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                                                    />
                                                </IconButton>
                                            </Tooltip>
                                        </Grid>}

                                    
                                    <ExportPopover
                                        handleDowloadTemplate={
                                            handleDowloadTemplate
                                        }
                                    />

                                    <Grid item>
                                        <Tooltip title="Follow">
                                            <IconButton aria-label="follow" size='small' onClick={handleBulkFlag}>
                                                <FlagIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </Grid>

                                    <Grid item>
                                        <Tooltip title="Acknowledge">
                                            <IconButton aria-label="acknowledge" size='small' onClick={handleBulkAcknowledge}>
                                                <CheckCircleIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </Grid>
                                </>
                            }
                            
                            loading={claimAnalysisDownloadReportPending || isPendingUpload!==null}
                            actionLevelGrid={ () =>
                                <Grid container>
                                    <Grid item md={12}>
                                    <h6 className="">{`Total Records : ${(claimsData?.length > 0 && claimPageRecord?.totalRecords) ? claimPageRecord?.totalRecords : 0}`}</h6>
                                    </Grid>
                                </Grid>
                            }
                        />

                        <ClaimAnalysisDataGrid
                        rcmAssignmentData={rcmAssignmentData}
                            defaultCustomFilter={defaultCustomFilters}
                            selectedClaimsIdsPtr={selectedClaimsIdsPtr}
                            data={claimsData}
                            handleAcknowledge={handleAcknowledge}
                            handleSingleFlag={handleSingleFlag}
                            loading={ Boolean(pending) || !notIsSwitchingClientAsPerUserRequest() || isPendingUpload!==null}
                            filtersPtr={ filtersPtr }

                            paginationFilters={tableFilter}
                            paginationPtr={tableFilterPtr.at('pagination')}
                            sortPtr={tableFilterPtr.at('sort')}
                            refreshCriteriaList={ setDefault => {
                                setDefault && tableFilterPtr.at('isFirstTime').set(true);
                                reloadCriteriaList();
                            }}
                            showSearchFilters={true}
                            selectedCriteria={ selectedCriteria }
                            filteredUsers={userData}
                        />
                    </Stack>

                </PortletBody>
            </Portlet>
            </Box>

                {/* AI Chat Section - fixed width sidebar or full width when expanded */}
                {aiModeEnabled && (
                    <AIChatSection 
                        open={aiModeEnabled}
                        onClose={() => {
                            setAiModeEnabled(false);
                            setIsChatExpanded(false);
                        }}
                        selectedClaimIds={selectedClaimsIdsPtr?.value || []}
                        totalClaimsCount={claimPageRecord?.totalRecords || 0}
                        externalMessage={aiMessageToSend}
                        onMessageSent={() => {
                            setAiCustomFilter('');
                            setAiMessageToSend('');
                        }}
                        onFiltersUpdate={handleAIFiltersUpdate}
                        isExpanded={isChatExpanded}
                        onExpandToggle={handleChatExpandToggle}
                        onClearAllFilters={handleClearAllFilters}
                        preAiModeFilters={preAiModeFilters}
                    />
                )}
            </Box>

            {/* Dialogs - placed outside the flex container */}
            <AcknowledgeDialog
                open={dialogsOpenPtr.value.acknowledgeDialog}
                handleClose={handleAcknowledgeClose}
                selectedItemsPtr={selectedClaimsIdsPtr}
            />

            <FlagDialog
                handleClose={handleAcknowledgeClose}
                openPtr={dialogsOpenPtr.at('flagDialog')}
                selectedItemsPtr={selectedClaimsIdsPtr}
            />

            <ProcessClaimDialog
                openPtr={dialogsOpenPtr.at('processClaimsDialog')
                    .onChange( open => {
                        if (!open) {
                            resetClaimProcessError();                        
                        }
                    })
                }
                
                selectedItemsPtr={selectedClaimsIdsPtr}

                handleProcessClaimCallback={handleProcessClaimCallback}
                claimsData={claimsData}
                filter={tableFilter.filters}
            />

            <UpdateStatusDialog 
                openPtr={dialogsOpenPtr.at('updateStatusDialog')}
                selectedItemsIdsPtr={selectedClaimsIdsPtr}
                claimStatusList={claimStatusList?.map((item)=>({label:item.name,value:item.id,name:item.name}))} 
                handleRefresh={handleRefresh}
            />

            <ClaimAssignmentDialog 
                openPtr={dialogsOpenPtr.at('assignClaimsDialog')}
                handleClose={()=>  dialogsOpenPtr.at('assignClaimsDialog').set(false)}
                selectedClaimsIds={selectedClaimsIdsPtr?.value}
                callBack={handleRefresh}
                dataUsers={userData}
                needRefetch={false}
            
            />
          {canAccessRCM &&  <RCMTeamAssignmentDialog selectedClaimsIds={selectedClaimsIdsPtr?.value} openPtr={dialogsOpenPtr.at('assignRcmTeamsDialog')} handleClose={()=>  dialogsOpenPtr.at('assignRcmTeamsDialog').set(false)}
            callBack={handleRefresh} rcmAssignmentData={rcmAssignmentData}
/>}

            <IgnoreFiltersDialog
                filtersPtr={
                    filtersPtr.onChange( obj => {
                        if(obj?.ignoreAcknowledgement){
                            handleRefresh()
                        }
                    })
                }
            />
        </div>
    );
}